import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, Sparkles, LogOut, Trash2, Focus } from 'lucide-react';
import confetti from 'canvas-confetti';
import TaskInput from '../components/TaskInput';
import TaskList from '../components/TaskList';
import StatsWidget from '../components/StatsWidget';
import PomodoroTimer from '../components/PomodoroTimer';
import RecentTasks from '../components/RecentTasks';
import AILoadingOverlay from '../components/AILoadingOverlay';
import NudgeSettings from '../components/NudgeSettings';
import ShareWinCard from '../components/ShareWinCard';
import { ThemeSelector, THEMES } from '../components/ThemeSelector';
import WelcomeTip from '../components/WelcomeTip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInput, setShowInput] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [firstStepMode, setFirstStepMode] = useState(() => {
    return localStorage.getItem('firstStepMode') === 'true';
  });
  const [shareWinData, setShareWinData] = useState(null);
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Load saved theme from localStorage
    const saved = localStorage.getItem('taskSplitterTheme');
    return saved || 'default';
  });

  const theme = THEMES.find(t => t.id === currentTheme) || THEMES[0];

  // Save theme to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('taskSplitterTheme', currentTheme);
  }, [currentTheme]);

  // Save firstStepMode to localStorage
  useEffect(() => {
    localStorage.setItem('firstStepMode', firstStepMode.toString());
  }, [firstStepMode]);

  useEffect(() => {
    loadUserAndTasks();
  }, []);

  const loadUserAndTasks = async () => {
    try {
      const [userRes, tasksRes, statsRes] = await Promise.all([
        api.get(`/api/auth/me`),
        api.get(`/api/tasks`),
        api.get(`/api/dashboard/stats`),
      ]);
      setUser(userRes.data);
      setTasks(tasksRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post(`/api/auth/logout`);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/');
    }
  };

  const handleCreateTask = async (title) => {
    try {
      const response = await api.post(`/api/tasks`, { title, parent_id: null });
      await loadUserAndTasks();
      toast.success('Task created!');
      setShowInput(false);
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    }
  };

  const handleAIBreakdown = async (title) => {
    try {
      setAiLoading(true);
      const response = await api.post(`/api/tasks/breakdown`, { task_title: title });
      await loadUserAndTasks();
      toast.success('Task breakdown created!');
      setShowInput(false);
    } catch (error) {
      console.error('Failed to breakdown task:', error);
      toast.error('AI breakdown failed. Try again or create manually.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleToggleComplete = async (taskId, completed) => {
    try {
      await api.put(`/api/tasks/${taskId}`, { completed: !completed });
      const freshData = await api.get(`/api/tasks`);
      const freshTasks = freshData.data;
      setTasks(freshTasks);

      // Check if completing this task finishes all subtasks of its parent
      if (!completed) {
        const toggledTask = freshTasks.find(t => t.task_id === taskId);
        const parentId = toggledTask?.parent_id;
        if (parentId) {
          const siblings = freshTasks.filter(t => t.parent_id === parentId);
          const allDone = siblings.length > 0 && siblings.every(t => t.completed);
          if (allDone) {
            confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
            toast.success("All subtasks done! You crushed it.", { duration: 5000 });
          }
        }
      }

      // Refresh stats too
      const statsRes = await api.get(`/api/dashboard/stats`);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await api.delete(`/api/tasks/${taskId}`);
      await loadUserAndTasks();
      toast.success('Task deleted');
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleUpdateStatus = async (taskId, status) => {
    try {
      await api.put(`/api/tasks/${taskId}`, { status });
      await loadUserAndTasks();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleReorder = async (taskIds) => {
    try {
      // Optimistic local reorder
      setTasks(prev => {
        const reordered = [...prev];
        taskIds.forEach((id, idx) => {
          const task = reordered.find(t => t.task_id === id);
          if (task) task.order = idx;
        });
        return reordered.sort((a, b) => (a.order || 0) - (b.order || 0));
      });
      await api.post('/api/tasks/reorder', { task_ids: taskIds });
    } catch (error) {
      console.error('Failed to reorder:', error);
      await loadUserAndTasks();
    }
  };

  const handleShareWin = (task, childTasks) => {
    setShareWinData({ task, childTasks });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E07A5F] mx-auto mb-4"></div>
          <p className="text-stone-500 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Loading your tasks...
          </p>
        </div>
      </div>
    );
  }

  const rootTasks = tasks.filter((t) => !t.parent_id);

  return (
    <div className={`min-h-screen ${theme.colors.bg}`} data-testid="dashboard">
      <WelcomeTip />
      
      {/* Header */}
      <div className={`border-b ${theme.colors.border} ${theme.colors.surface}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {user?.picture && (
                <img
                  src={user.picture}
                  alt={user.name}
                  className={`w-12 h-12 rounded-full border-2 ${theme.colors.border}`}
                  data-testid="user-avatar"
                />
              )}
              <div>
                <h1
                  className={`text-2xl sm:text-3xl tracking-tight font-medium ${theme.colors.textPrimary}`}
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                  data-testid="dashboard-title"
                >
                  {user?.name}'s Tasks
                </h1>
                <p className={`text-sm ${theme.colors.textSecondary}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Break it down, get it done
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ThemeSelector currentTheme={currentTheme} onThemeChange={setCurrentTheme} />
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className={`p-2 rounded-md ${theme.colors.textSecondary} hover:${theme.colors.textPrimary} transition-colors`}
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Dashboard */}
        {stats && <StatsWidget stats={stats} theme={theme} />}

        {/* Add Task Section - right below dashboard */}
        <div className={`mt-6 ${theme.colors.surface} border ${theme.colors.border} rounded-xl p-6 sm:p-8 shadow-sm`}>
          {!showInput ? (
            <button
              onClick={() => setShowInput(true)}
              data-testid="add-task-btn"
              className={`w-full text-left py-4 text-xl sm:text-2xl font-medium transition-colors hover:opacity-80`}
              style={{ fontFamily: 'Manrope, sans-serif', color: theme?.colors.rawTextSecondary || '#78716c' }}
            >
              + Add a new task...
            </button>
          ) : (
            <TaskInput
              onCreateWithAI={handleAIBreakdown}
              onCancel={() => setShowInput(false)}
              aiLoading={aiLoading}
              theme={theme}
            />
          )}
        </div>

        {/* AI Loading Overlay */}
        {aiLoading && (
          <div className="mt-6">
            <AILoadingOverlay theme={theme} />
          </div>
        )}

        {/* Tasks List */}
        <div className="mt-6">
          {/* First Step Mode Toggle */}
          {rootTasks.length > 0 && (
            <div className="flex items-center justify-between mb-4">
              <h2
                className={`text-lg font-medium ${theme.colors.textPrimary}`}
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {firstStepMode ? 'Your Next Step' : 'All Tasks'}
              </h2>
              <button
                onClick={() => setFirstStepMode(!firstStepMode)}
                data-testid="first-step-toggle"
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  firstStepMode
                    ? `${theme.colors.primary} text-white`
                    : `${theme.colors.surface} border ${theme.colors.border} ${theme.colors.textSecondary}`
                }`}
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <Focus className="w-4 h-4" />
                Just the first step
              </button>
            </div>
          )}

          {rootTasks.length === 0 ? (
            <div className="text-center py-8" data-testid="empty-state">
              <p
                className={`text-xl font-medium ${theme.colors.textPrimary} mb-2`}
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Feeling overwhelmed?
              </p>
              <p className={`text-base ${theme.colors.textSecondary}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Let's break it down. Add your first task above to get started.
              </p>
            </div>
          ) : firstStepMode ? (
            (() => {
              const findFirstStep = () => {
                for (const root of rootTasks) {
                  if (root.completed) continue;
                  const children = tasks.filter(t => t.parent_id === root.task_id);
                  if (children.length === 0) return { step: root, parent: null };
                  for (const child of children) {
                    if (child.completed) continue;
                    const grandchildren = tasks.filter(t => t.parent_id === child.task_id);
                    if (grandchildren.length === 0) return { step: child, parent: root };
                    for (const gc of grandchildren) {
                      if (!gc.completed) return { step: gc, parent: child };
                    }
                  }
                }
                return null;
              };
              const result = findFirstStep();
              if (!result) {
                return (
                  <div className="text-center py-8" data-testid="all-done-state">
                    <p className={`text-xl font-medium ${theme.colors.textPrimary} mb-2`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                      All done! Nothing left to do.
                    </p>
                  </div>
                );
              }
              return (
                <div
                  className={`${theme.colors.surface} border-2 border-[#E07A5F] rounded-xl p-5 sm:p-8 shadow-md`}
                  data-testid="first-step-card"
                >
                  {result.parent && (
                    <p className={`text-xs uppercase tracking-widest font-semibold ${theme.colors.textSecondary} mb-3`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      From: {result.parent.title}
                    </p>
                  )}
                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={result.step.completed}
                      onChange={() => handleToggleComplete(result.step.task_id, result.step.completed)}
                      className="task-checkbox"
                      data-testid="first-step-checkbox"
                    />
                    <h3
                      className={`text-lg sm:text-2xl font-medium ${theme.colors.textPrimary}`}
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                      data-testid="first-step-title"
                    >
                      {result.step.title}
                    </h3>
                  </div>
                  <p className={`text-sm ${theme.colors.textSecondary} mt-3`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Just this one thing. Check it off and the next step appears.
                  </p>
                </div>
              );
            })()
          ) : (
            <div className="space-y-6">
              {rootTasks.map((task) => (
                <TaskList
                  key={task.task_id}
                  task={task}
                  allTasks={tasks}
                  onToggleComplete={handleToggleComplete}
                  onDelete={handleDeleteTask}
                  onUpdateStatus={handleUpdateStatus}
                  onReorder={handleReorder}
                  onShareWin={handleShareWin}
                  theme={theme}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pomodoro Timer, Recent Tasks, and Nudge */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PomodoroTimer theme={theme} />
          <div className="space-y-6">
            <NudgeSettings theme={theme} />
            <RecentTasks theme={theme} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className={`mt-auto border-t ${theme.colors.border} py-8 pb-20`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center gap-4">
          <div className="flex items-center gap-4 -translate-x-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_task-breakdown-17/artifacts/c663f9at_6_20260405_101800_0005.png"
              alt="Procrastination Popper"
              className="w-36 h-36 object-contain"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
            />
          </div>
          <p 
            className={`text-sm ${theme.colors.textSecondary} text-center`} 
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            Procrastination Popper
            <br />
            Conquer Analysis Paralysis
          </p>
        </div>
      </footer>

      {/* Share Win Modal */}
      {shareWinData && (
        <ShareWinCard
          task={shareWinData.task}
          childTasks={shareWinData.childTasks}
          theme={theme}
          onClose={() => setShareWinData(null)}
        />
      )}
    </div>
  );
}

export default Dashboard;